/**
 * Services Management Page
 * List and manage services and categories
 */

import { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, TagIcon } from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';
import ServiceForm from '../../components/admin/ServiceForm';
import CategoryManager from '../../components/admin/CategoryManager';

export default function ServicesManagement() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingService, setEditingService] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('[ServicesManagement] Fetching data...');
      const [servicesRes, categoriesRes] = await Promise.all([
        adminAPI.services.getAll(),
        adminAPI.categories.getAll()
      ]);
      console.log('[ServicesManagement] Services response:', servicesRes.data);
      console.log('[ServicesManagement] Categories response:', categoriesRes.data);
      console.log('[ServicesManagement] Services array:', servicesRes.data.services);
      setServices(servicesRes.data.services || []);
      setCategories(categoriesRes.data.categories || []);
      console.log('[ServicesManagement] State updated - services count:', servicesRes.data.services?.length);
    } catch (err) {
      console.error('[ServicesManagement] Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = () => {
    setEditingService(null);
    setShowServiceForm(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowServiceForm(true);
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await adminAPI.services.delete(id);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete service');
    }
  };

  const handleToggleService = async (id) => {
    try {
      await adminAPI.services.toggle(id);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle service');
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingService) {
        await adminAPI.services.update(editingService.id, formData);
      } else {
        await adminAPI.services.create(formData);
      }
      setShowServiceForm(false);
      setEditingService(null);
      fetchData();
    } catch (err) {
      throw err;
    }
  };

  const handleCategoryUpdate = () => {
    fetchData();
  };

  // Group services by category
  console.log('[ServicesManagement] Grouping services - total:', services.length);
  console.log('[ServicesManagement] Categories:', categories.length);
  const groupedServices = categories.reduce((acc, category) => {
    const categoryServices = services.filter(s => s.serviceCategoryId === category.id);
    console.log(`[ServicesManagement] Category "${category.name}" (id: ${category.id}) has ${categoryServices.length} services`);
    if (categoryServices.length > 0) {
      console.log(`[ServicesManagement] Services:`, categoryServices.map(s => ({ id: s.id, name: s.name, categoryId: s.serviceCategoryId })));
    }
    acc[category.id] = {
      category,
      services: categoryServices
    };
    return acc;
  }, {});

  // Uncategorized services
  const uncategorizedServices = services.filter(s => !s.serviceCategoryId);
  console.log('[ServicesManagement] Uncategorized services:', uncategorizedServices.length);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Services Management</h1>
          <p className="text-gray-600">Manage your service catalog and categories</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowCategoryManager(true)}
            className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <TagIcon className="w-5 h-5 mr-2" />
            Manage Categories
          </button>
          <button
            onClick={handleAddService}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Service
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {services.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <ServiceIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No services yet</h3>
          <p className="text-gray-500 mb-6">
            Get started by adding your first service
          </p>
          <button
            onClick={handleAddService}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700"
          >
            Add Service
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Services grouped by category */}
          {categories.map(category => {
            const group = groupedServices[category.id];
            if (!group || group.services.length === 0) return null;

            return (
              <div key={category.id}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {category.name}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.services.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      onEdit={() => handleEditService(service)}
                      onDelete={() => handleDeleteService(service.id)}
                      onToggle={() => handleToggleService(service.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Uncategorized services */}
          {uncategorizedServices.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Uncategorized
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {uncategorizedServices.map((service) => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onEdit={() => handleEditService(service)}
                    onDelete={() => handleDeleteService(service.id)}
                    onToggle={() => handleToggleService(service.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Service Form Modal */}
      {showServiceForm && (
        <ServiceForm
          service={editingService}
          categories={categories}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowServiceForm(false);
            setEditingService(null);
          }}
        />
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          categories={categories}
          onUpdate={handleCategoryUpdate}
          onClose={() => setShowCategoryManager(false)}
        />
      )}
    </div>
  );
}

function ServiceCard({ service, onEdit, onDelete, onToggle }) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">
            {service.name}
          </h3>
          {service.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {service.description}
            </p>
          )}
        </div>
        <div className="flex space-x-1 ml-2">
          <button
            onClick={onEdit}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            title="Edit service"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
            title="Delete service"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Price:</span>
          <span className="font-semibold text-gray-900">
            ${parseFloat(service.price).toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Duration:</span>
          <span className="text-gray-900">{service.duration} minutes</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={onToggle}
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
            service.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {service.isActive ? 'Active' : 'Inactive'}
        </button>
      </div>
    </div>
  );
}

// Service icon component
function ServiceIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}
